import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  ViewChild
} from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { Popover } from 'bootstrap';
import { isFunction, isObject } from 'lodash';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';

@Component({
  selector: 'app-action-cell-renderer',
  templateUrl: './action-cell-renderer.component.html',
  styleUrls: ['./action-cell-renderer.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionCellRendererComponent
  implements ICellRendererAngularComp, OnInit
{
  params!: ICellRendererParams;
  Popovers?: Popover[];

  @ViewChild('actionCellRenderer') actionCellRenderer?: ElementRef;

  actionClicked(action: GridActionColumnActions, event: Event) {
    const actionEvent: EventEmitter<{
      action: GridActionColumnActions;
      rowData: any;
      rowNode: IRowNode;
      cellRendererParams: ICellRendererParams;
    }> = this.params.context.componentParent.actionEvent;
    if (!actionEvent) {
      throw new Error(
        'actionEvent Event Emitter is not configured on grid for action column(s).'
      );
    }

    // When a grid action item is clicked, hide any shown popovers in the cell.
    if (this.Popovers) {
      this.Popovers.forEach((popover) => popover.hide());
    }

    const { data, node } = this.params;

    actionEvent.emit({
      action,
      rowData: this.params.data,
      rowNode: node as IRowNode,
      cellRendererParams: this.params
    });
  }

  actions: GridActionColumnActions[] = [];
  TooltipMap: { [key in GridActionColumnActions]: string } = {
    view: '',
    edit: '',
    search: '',
    cancel: '',
    stop: '',
    add: '',
    remove: '',
    delete: '',
    archive: '',
    unarchive: '',
    download: '',
    upload: '',
    award: '',
    play: '',
    plane: '',
    link: '',
    unlink: '',
    refresh: '',
    invite: ''
  };

  actionToClassMap: {
    view: 'bi bi-eye-fill';
    edit: 'bi bi-pencil-fill';
    search: 'bi bi-search';
    cancel: 'bi bi-trash-fill';
    stop: 'bi bi-stop-circle';
    add: 'bi bi-plus-circle-fill';
    remove: 'bi bi-dash-circle-fill';
    delete: 'bi bi-trash-fill';
    archive: 'bi bi-archive-fill';
    unarchive: 'bi bi-box-arrow-in-down-left';
    download: 'bi bi-download';
    upload: 'bi bi-upload';
    award: 'bi bi-trophy-fill';
    play: 'bi bi-play-fill fs-2rem';
    plane: 'bi bi-send-fill';
    refresh: 'bi bi-arrow-repeat';
    invite: 'bi bi-envelope-fill';
    // Material UI
    link: 'Link';
    unlink: 'link_off';
  } = {
    view: 'bi bi-eye-fill',
    edit: 'bi bi-pencil-fill',
    search: 'bi bi-search',
    cancel: 'bi bi-trash-fill',
    stop: 'bi bi-stop-circle',
    add: 'bi bi-plus-circle-fill',
    remove: 'bi bi-dash-circle-fill',
    delete: 'bi bi-trash-fill',
    archive: 'bi bi-archive-fill',
    unarchive: 'bi bi-box-arrow-in-down-left',
    download: 'bi bi-download',
    upload: 'bi bi-upload',
    award: 'bi bi-trophy-fill',
    play: 'bi bi-play-fill fs-2rem',
    plane: 'bi bi-send-fill',
    refresh: 'bi bi-arrow-repeat',
    invite: 'bi bi-envelope-fill',
    link: 'Link',
    unlink: 'link_off'
  };

  isBootstrapIcon(action: string): boolean {
    return action.includes('bi bi');
  }

  cellInitialization() {
    if (!this.params) {
      throw new Error('Cell initialization is missing required parameters.');
    }

    const { api, colDef, data } = this.params;

    const {
      ColumnIdToColumnActions,
      getCalculatedColumnActions,
      getCalculatedColumnTooltipMap,
      TooltipMap
    }: {
      ColumnIdToColumnActions: GridActionColumnMap;
      getCalculatedColumnActions: ({
        rowData
      }: {
        rowData: any;
      }) => null | GridActionColumnActions[];
      getCalculatedColumnTooltipMap: ({
        rowData
      }: {
        rowData: any;
      }) => null | Partial<{ [key in GridActionColumnActions]: string }>;
      TooltipMap: { [key in GridActionColumnActions]: string };
    } = this.params.context.componentParent;

    if (!isObject(ColumnIdToColumnActions)) {
      throw new Error(
        'The ColumnIdToColumnActions map has not been set on a grid with action column(s).'
      );
    }

    if (!colDef) {
      throw new Error(
        'Column Definition has not been configured on action column(s).'
      );
    }

    const { colId } = colDef;

    if (!colId) {
      throw new Error(
        'Column Definition is missing column id for action column(s).'
      );
    }

    if (isFunction(getCalculatedColumnActions)) {
      this.actions =
        getCalculatedColumnActions({ rowData: data }) ||
        ColumnIdToColumnActions[colId];
    } else {
      this.actions = ColumnIdToColumnActions[colId];
    }

    if (isFunction(getCalculatedColumnTooltipMap)) {
      Object.assign(
        this.TooltipMap,
        getCalculatedColumnTooltipMap({ rowData: data }) || TooltipMap || {}
      );
    } else {
      Object.assign(this.TooltipMap, TooltipMap || {});
    }
  }

  agInit(params: ICellRendererParams) {
    this.params = params;
    this.cellInitialization();
  }

  refresh(params: ICellRendererParams) {
    this.params = params;
    this.cellInitialization();
    return true;
  }

  ngAfterViewInit(): void {
    if (this.actionCellRenderer) {
      const popoverTriggerList: Element[] =
        this.actionCellRenderer.nativeElement.querySelectorAll(
          '[name="popover"]'
        );

      this.Popovers = Array.from(popoverTriggerList).map((popoverTriggerEl) => {
        return new Popover(popoverTriggerEl, { trigger: 'hover' });
      });
    }
  }

  ngOnInit(): void {}
}
